import { useCallback, useState, useEffect } from "react";
import { postApi } from "../../../../utils/api.ts";
import { API_FORGOT_PASSWORD, API_VERIFY_OTP } from "../../../../../config/Endpoints.js";
import { useNavigate } from "react-router-dom";

interface ForgotPasswordResponse {
  message: string;
  token?: string;
  email?: string;
}

interface UseForgotPasswordReturn {
  loading: boolean;
  error: string | null;
  isOTPSent: boolean;
  forgotToken: string | null;
  timeLeft: number;
  isResendActive: boolean;
  sendOTP: (email: string) => Promise<void>;
  verifyOTP: (otp: string, email: string) => Promise<void>;
  resendOTP: (email: string) => Promise<void>;
}

const RESEND_TIMER_SECONDS = 120;
const ERROR_AUTO_CLEAR_MS = 4000;

export function useForgotPassword(): UseForgotPasswordReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOTPSent, setIsOTPSent] = useState(false);
  const [forgotToken, setForgotToken] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(RESEND_TIMER_SECONDS);
  const [isResendActive, setIsResendActive] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let timerId: number;
    if (isOTPSent && timeLeft > 0) {
      timerId = window.setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsResendActive(true);
    }
    return () => {
      if (timerId) window.clearInterval(timerId);
    };
  }, [isOTPSent, timeLeft]);

  const sendOTP = useCallback(async (email: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await postApi<ForgotPasswordResponse>(
        `${API_FORGOT_PASSWORD}?email=${email}`,
        undefined,
        {},
      );

      if (data.message === "OTP Sent Successfully.") {
        setIsOTPSent(true);
        setForgotToken(data.token ?? null);
        setTimeLeft(RESEND_TIMER_SECONDS);
        setIsResendActive(false);
      } else {
        throw new Error(data.message);
      }
    } catch (e: any) {
      const message = e.message ?? "Something went wrong";
      setError(message);
      window.setTimeout(() => setError(null), ERROR_AUTO_CLEAR_MS);
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyOTP = useCallback(
    async (otp: string, email: string) => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await postApi<ForgotPasswordResponse>(
          `${API_VERIFY_OTP}?email=${email}&OTP=${otp}&token=${forgotToken}`,
          undefined,
          {},
        );

        if (data.message === "Invalid OTP!") {
          throw new Error(data.message);
        } else {
          navigate("/reset-password", {
            state: {
              email: data.email,
              token: forgotToken,
            },
          });
        }
      } catch (e: any) {
        const message = e.message ?? "Invalid OTP";
        setError(message);
        window.setTimeout(() => setError(null), ERROR_AUTO_CLEAR_MS);
      } finally {
        setLoading(false);
      }
    },
    [forgotToken, navigate],
  );

  const resendOTP = useCallback(async (email: string) => {
    await sendOTP(email);
  }, [sendOTP]);

  return {
    loading,
    error,
    isOTPSent,
    forgotToken,
    timeLeft,
    isResendActive,
    sendOTP,
    verifyOTP,
    resendOTP,
  };
}
